/**
 * Small shared pieces of the iManage area: the iManage mark, a glyph per file
 * kind, the client/matter chip, and the inline retryable error row.
 *
 * @module components/apps/imanage/IManageParts
 */
import type { IManageEntry } from "@amoreg/contracts/imanage";
import {
  BriefcaseIcon,
  FileArchiveIcon,
  FileIcon,
  FileImageIcon,
  FileSpreadsheetIcon,
  FileTextIcon,
  FolderIcon,
  MailIcon,
  PresentationIcon,
  RotateCwIcon,
  TriangleAlertIcon,
} from "lucide-react";

import { cn } from "~/lib/utils";
import { IntegrationIcon } from "../integrations/IntegrationIcon";
import { Button } from "../../ui/button";
import { clientMatter, fileKind, fileKindLabel, type FileKind } from "./imanageFormat";

export function IManageMark({ className }: { readonly className?: string }) {
  return (
    <IntegrationIcon
      integration={{ id: "imanage", name: "iManage", iconUrl: null, toolkitSlug: null }}
      className={cn("p-1.5", className)}
    />
  );
}

const GLYPHS: Record<FileKind, { icon: typeof FileIcon; tone: string }> = {
  folder: { icon: FolderIcon, tone: "text-muted-foreground" },
  workspace: { icon: BriefcaseIcon, tone: "text-muted-foreground" },
  word: { icon: FileTextIcon, tone: "text-info" },
  pdf: { icon: FileTextIcon, tone: "text-destructive" },
  email: { icon: MailIcon, tone: "text-warning" },
  sheet: { icon: FileSpreadsheetIcon, tone: "text-success" },
  slides: { icon: PresentationIcon, tone: "text-warning" },
  text: { icon: FileTextIcon, tone: "text-muted-foreground" },
  image: { icon: FileImageIcon, tone: "text-muted-foreground" },
  archive: { icon: FileArchiveIcon, tone: "text-muted-foreground" },
  other: { icon: FileIcon, tone: "text-muted-foreground" },
};

/** A 16px glyph for an item's kind, labelled for screen readers by the row. */
export function FileGlyph({
  entry,
  className,
}: {
  readonly entry: Pick<IManageEntry, "kind" | "extension" | "email">;
  readonly className?: string;
}) {
  const { icon: Icon, tone } = GLYPHS[fileKind(entry)];
  return <Icon aria-hidden className={cn("size-4 shrink-0", tone, className)} />;
}

export { fileKindLabel };

/** The client-matter number of a workspace, as a quiet mono chip. */
export function MatterNumber({
  entry,
  className,
}: {
  readonly entry: Pick<IManageEntry, "client" | "matter">;
  readonly className?: string;
}) {
  const label = clientMatter(entry);
  if (!label) return null;
  return (
    <span
      className={cn(
        "shrink-0 font-mono text-[11px] tabular-nums tracking-tight text-muted-foreground",
        className,
      )}
    >
      {label}
    </span>
  );
}

/** One quiet line with a retry, for a failed read inside a page. */
export function InlineError({
  message,
  onRetry,
  className,
}: {
  readonly message: string;
  readonly onRetry?: () => void;
  readonly className?: string;
}) {
  return (
    <div
      role="alert"
      className={cn(
        "flex items-center gap-2.5 rounded-lg border border-destructive/25 bg-destructive/[0.04] px-3 py-2 text-[13px]",
        className,
      )}
    >
      <TriangleAlertIcon aria-hidden className="size-4 shrink-0 text-destructive" />
      <span className="min-w-0 flex-1 text-foreground">{message}</span>
      {onRetry ? (
        <Button variant="ghost" size="xs" onClick={onRetry}>
          <RotateCwIcon aria-hidden />
          Retry
        </Button>
      ) : null}
    </div>
  );
}
